import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Tooltip,
  Paper,
} from '@mui/material';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { ConstantActions } from '../../Services/_actions/index.js';
import { globalpermissions } from '../../Services/Permission.js';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingButton from '../Shared/Loading/index.tsx';
import { AiActions } from '../../Services/_actions/index.js';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

// ─── Shared small-font style ──────────────────────────────────────────────────
const fieldSx = {
  '& .MuiInputBase-input': { fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.82rem' },
};

// ─── Safe immutable nested setter (NO JSON.parse/stringify) ──────────────────
// Uses structured path like 'topics.0.subtopics.1.subtopic'
// Performs shallow clone at each level so React detects the change,
// but never deep-clones the ENTIRE object — this is what caused the stack overflow.
function setIn(obj: any, pathStr: string, value: any): any {
  const keys = pathStr.split('.');
  function recurse(current: any, depth: number): any {
    const key = keys[depth];
    if (depth === keys.length - 1) {
      if (Array.isArray(current)) {
        const copy = [...current];
        copy[Number(key)] = value;
        return copy;
      }
      return { ...current, [key]: value };
    }
    const next = recurse(current[key], depth + 1);
    if (Array.isArray(current)) {
      const copy = [...current];
      copy[Number(key)] = next;
      return copy;
    }
    return { ...current, [key]: next };
  }
  return recurse(obj, 0);
}

// ─── Debounce hook ────────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Helper ───────────────────────────────────────────────────────────────────
const makeShortId = (prefix = 'pre') => `${prefix}_${Math.random().toString(36).slice(2, 8)}`;

const emptySubtopic = () => ({
  subtopic: '',
  prerequisites: '',
  prerequisiteObjects: [],
  marks: '',
});

const emptyTopic = () => ({
  topic: '',
  topic_prerequisites: '',
  marks: '',
  subtopics: [emptySubtopic()],
  learning_material: '',
  description: '',
});

const initialFormData = {
  subject: '',
  class: '',
  board: '',
  books: '',
  instruction_set: '',
  question_generator_instruction: '',
  diagram_instruction: '',
  video_instruction: '',
  verification_instruction: '',
  step_by_step_solution_instruction: '',
  step_by_step_reasoning_instruction: '',
  sample_example_instruction: '',
  lead_form_id: '',
  campaign_id: '',
  test_instruction_set: '',
  test_lead_form_id: '',
  test_campaign_id: '',
  topics: [emptyTopic()],
};

// ─────────────────────────────────────────────────────────────────────────────
export default function CreateSubjectMap() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { subjectData } = state || {};

  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();
  const [jsonInput, setJsonInput] = useState('');
const [copiedJson, setCopiedJson] = useState(false);
  const loggedInUser = useRecoilValue(userInformationAtom);
  const constantActions = ConstantActions();
  const isSuperUser = loggedInUser?.is_superuser === true || loggedInUser?.profile?.name === 'Admin';
  const hasClassListPermission = isSuperUser || constantActions.userHasPermission([globalpermissions?.performancePermissions?.classListAccess]);

  const loggedInOrgId = loggedInUser?.profile?.organisation?.organisation_id || '';

  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Start all topics collapsed when there are many — keeps page responsive
  const [collapsedTopics, setCollapsedTopics] = useState<Record<number, boolean>>({});

  const [formData, setFormData] = useState<any>(initialFormData);

  // Debounce formData for the JSON preview so typing doesn't lag the whole page
  const debouncedFormData = useDebounce(formData, 400);

  // ─── Load edit data ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!subjectData) return;

    setIsEditMode(true);
    setEditId(subjectData._id);

    const mappedTopics = (subjectData.topics || []).map((t: any) => ({
      topic: t.topic || '',
      topic_prerequisites: Array.isArray(t.topic_prerequisites)
        ? t.topic_prerequisites.join(', ')
        : t.topic_prerequisites || '',
      marks: t.marks || '',
      learning_material: Array.isArray(t.learning_material)
        ? t.learning_material.join(', ')
        : t.learning_material || '',
      description: t.description || '',
      subtopics: (t.subtopics || []).map((st: any) => ({
        subtopic: st.subtopic || '',
        prerequisites: (st.prerequisites || []).map((o: any) => o.name).join(', '),
        prerequisiteObjects: st.prerequisites || [],
        marks: st.marks || '',
      })),
    }));

    const mapped = {
      subject: subjectData.subject || '',
      class: subjectData.class || '',
      board: subjectData.board || '',
      instruction_set: subjectData.instruction_set || '',
      question_generator_instruction: subjectData.question_generator_instruction || '',
      verification_instruction: subjectData.verification_instruction || '',
      step_by_step_solution_instruction: subjectData.step_by_step_solution_instruction || '',
      step_by_step_reasoning_instruction: subjectData.step_by_step_reasoning_instruction || '',
      sample_example_instruction: subjectData.sample_example_instruction || '',
      lead_form_id: subjectData.lead_form_id || '',
      campaign_id: subjectData.campaign_id || '',
      test_instruction_set: subjectData.test_instruction_set || '',
      test_lead_form_id: subjectData.test_lead_form_id || '',
      test_campaign_id: subjectData.test_campaign_id || '',
      books: Array.isArray(subjectData.books)
        ? subjectData.books.join(', ')
        : subjectData.books || '',
      topics: mappedTopics,
      diagram_instruction: subjectData.diagram_instruction || '',
      video_instruction: subjectData.video_instruction || '',
    };

    if (isSuperUser && subjectData.organisation_id) {
      setSelectedOrganisation({ organisation_id: subjectData.organisation_id });
    }

    // Auto-collapse all topics when loading large data — prevents render freeze
    if (mappedTopics.length > 5) {
      const allCollapsed: Record<number, boolean> = {};
      mappedTopics.forEach((_: any, i: number) => { allCollapsed[i] = true; });
      setCollapsedTopics(allCollapsed);
    }

    console.log('Auto loaded data ====>', mapped);
    setFormData(mapped);
  }, [subjectData]);

  // ─── Form Update — uses setIn (no deep clone, no stack overflow) ─────────────
  const updateForm = useCallback((path: string, value: any) => {
    setFormData((prev: any) => {
      const updated = setIn(prev, path, value);
      // If editing prerequisites text, also clear the prerequisiteObjects sibling
      const keys = path.split('.');
      if (keys[keys.length - 1] === 'prerequisites') {
        const siblingPath = [...keys.slice(0, -1), 'prerequisiteObjects'].join('.');
        return setIn(updated, siblingPath, []);
      }
      return updated;
    });
  }, []);

  // ─── Topic helpers ────────────────────────────────────────────────────────────
  const addTopic = useCallback(() => {
    setFormData((prev: any) => ({
      ...prev,
      topics: [...prev.topics, emptyTopic()],
    }));
  }, []);

  const removeTopic = useCallback((i: number) => {
    setFormData((prev: any) => {
      if (prev.topics.length <= 1) {
        showSnackBar('At least one topic required', 'warning');
        return prev;
      }
      return { ...prev, topics: prev.topics.filter((_: any, idx: number) => idx !== i) };
    });
  }, [showSnackBar]);

  const addSubtopic = useCallback((tIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: [...newTopics[tIndex].subtopics, emptySubtopic()],
      };
      return { ...prev, topics: newTopics };
    });
  }, []);

  const removeSubtopic = useCallback((tIndex: number, stIndex: number) => {
    setFormData((prev: any) => {
      if (prev.topics[tIndex].subtopics.length <= 1) {
        showSnackBar('At least one subtopic required', 'warning');
        return prev;
      }
      const newTopics = [...prev.topics];
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: newTopics[tIndex].subtopics.filter((_: any, i: number) => i !== stIndex),
      };
      return { ...prev, topics: newTopics };
    });
  }, [showSnackBar]);

  // ─── Prerequisite helpers ─────────────────────────────────────────────────────
  // NOTE: These mutate the nested object then trigger a shallow re-render.
  // This is safe here because the objects are only used for the prereq tree UI.
  const addPrereqChild = useCallback((tIndex: number, stIndex: number, parentObj: any) => {
    parentObj.children.push({ id: makeShortId(), name: '', children: [] });
    setFormData((prev: any) => ({ ...prev }));
  }, []);

  const addRootPrereq = useCallback((tIndex: number, stIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      const st = newTopics[tIndex].subtopics[stIndex];
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: newTopics[tIndex].subtopics.map((s: any, i: number) =>
          i === stIndex
            ? { ...s, prerequisiteObjects: [...s.prerequisiteObjects, { id: makeShortId(), name: '', children: [] }] }
            : s
        ),
      };
      return { ...prev, topics: newTopics };
    });
  }, []);

  const updatePrereqName = useCallback((obj: any, value: string) => {
    obj.name = value;
    setFormData((prev: any) => ({ ...prev }));
  }, []);

  const removePrereqObj = useCallback((arr: any[], index: number) => {
    arr.splice(index, 1);
    setFormData((prev: any) => ({ ...prev }));
  }, []);

  const handleAutoGenerate = useCallback((tIndex: number, stIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      const st = { ...newTopics[tIndex].subtopics[stIndex] };
      if (!st.prerequisiteObjects.length) {
        st.prerequisiteObjects = [{ id: makeShortId(), name: '', children: [] }];
      }
      st.prerequisites = st.prerequisiteObjects.map((p: any) => p.name).join(', ');
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: newTopics[tIndex].subtopics.map((s: any, i: number) => i === stIndex ? st : s),
      };
      return { ...prev, topics: newTopics };
    });
  }, []);

  const handleClearAuto = useCallback((tIndex: number, stIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: newTopics[tIndex].subtopics.map((s: any, i: number) =>
          i === stIndex ? { ...s, prerequisiteObjects: [], prerequisites: '' } : s
        ),
      };
      return { ...prev, topics: newTopics };
    });
  }, []);

  // ─── JSON Import (paste textarea) ─────────────────────────────────────────────
  // Instruction / campaign fields are PRESERVED from current state on import.
  const handleJsonImport = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      importParsedJson(parsed);
    } catch {
      showSnackBar('Invalid JSON format', 'error');
    }
  };

  const importParsedJson = (parsed: any) => {
    if (!parsed.subject) return showSnackBar('Invalid JSON: missing subject field', 'error');

    const mappedTopics = (parsed.topics || []).map((t: any) => ({
      topic: t.topic || '',
      topic_prerequisites: Array.isArray(t.topic_prerequisites)
        ? t.topic_prerequisites.join(', ')
        : t.topic_prerequisites || '',
      marks: t.marks || '',
      learning_material: Array.isArray(t.learning_material)
        ? t.learning_material.join(', ')
        : t.learning_material || '',
      description: t.description || '',
      subtopics: (t.subtopics || []).map((st: any) => {
        let objs: any[] = [];
        if (Array.isArray(st.prerequisites)) {
          objs = st.prerequisites.map((p: any) => ({
            id: p.id || makeShortId(),
            name: p.name || '',
            children: p.children || [],
          }));
        } else if (typeof st.prerequisites === 'string') {
          objs = st.prerequisites
            .split(',')
            .map((s: string) => ({ id: makeShortId(), name: s.trim(), children: [] }));
        }
        return {
          subtopic: st.subtopic || '',
          prerequisites: objs.map((o: any) => o.name).join(', '),
          prerequisiteObjects: objs,
          marks: st.marks || '',
        };
      }),
    }));

    // Collapse all imported topics when there are many
    if (mappedTopics.length > 5) {
      const allCollapsed: Record<number, boolean> = {};
      mappedTopics.forEach((_: any, i: number) => { allCollapsed[i] = true; });
      setCollapsedTopics(allCollapsed);
    }

    setFormData((prev: any) => ({
      // Overwrite: subject / class / board / books / topics
      subject: parsed.subject || '',
      class: parsed.class || '',
      board: parsed.board || '',
      books: Array.isArray(parsed.books) ? parsed.books.join(', ') : parsed.books || '',
      topics: mappedTopics,
      // Preserve ALL instruction-related fields
      instruction_set: prev.instruction_set,
      question_generator_instruction: prev.question_generator_instruction,
      verification_instruction: prev.verification_instruction,
      step_by_step_solution_instruction: prev.step_by_step_solution_instruction,
      step_by_step_reasoning_instruction: prev.step_by_step_reasoning_instruction,
      sample_example_instruction: prev.sample_example_instruction,
      test_instruction_set: prev.test_instruction_set,
      // Preserve campaign / lead form IDs
      lead_form_id: prev.lead_form_id,
      campaign_id: prev.campaign_id,
      test_lead_form_id: prev.test_lead_form_id,
      test_campaign_id: prev.test_campaign_id,
      diagram_instruction: prev.diagram_instruction,
      video_instruction: prev.video_instruction,
    }));

    showSnackBar('JSON imported — instruction & campaign fields preserved', 'success');
  };

  // ─── Format for API ─────────────────────────────────────────────────────────
  const formatData = useCallback((fd = formData) => ({
    subject: fd.subject,
    class: fd.class,
    board: fd.board,
    books: (fd.books || '').split(',').map((b: string) => b.trim()).filter(Boolean),
    instruction_set: fd.instruction_set,
    question_generator_instruction: fd.question_generator_instruction,
    verification_instruction: fd.verification_instruction,
    step_by_step_solution_instruction: fd.step_by_step_solution_instruction,
    step_by_step_reasoning_instruction: fd.step_by_step_reasoning_instruction,
    sample_example_instruction: fd.sample_example_instruction,
    lead_form_id: fd.lead_form_id,
    campaign_id: fd.campaign_id,
    test_instruction_set: fd.test_instruction_set,
    diagram_instruction: fd.diagram_instruction,
    video_instruction: fd.video_instruction,
    test_lead_form_id: fd.test_lead_form_id,
    test_campaign_id: fd.test_campaign_id,
    ...(isSuperUser && selectedOrganisation?.organisation_id
      ? { organisation_id: selectedOrganisation.organisation_id }
      : (!isSuperUser && hasClassListPermission && loggedInOrgId)
        ? { organisation_id: loggedInOrgId }
        : {}),
    topics: (fd.topics || [])
      .filter((t: any) => t.topic.trim() !== '')
      .map((t: any) => ({
        topic: t.topic,
        topic_prerequisites: (t.topic_prerequisites || '')
          .split(',').map((x: string) => x.trim()).filter(Boolean),
        marks: t.marks ? Number(t.marks) : undefined,
        subtopics: (t.subtopics || [])
          .filter((st: any) => st.subtopic.trim() !== '')
          .map((st: any) => ({
            subtopic: st.subtopic,
            prerequisites: st.prerequisiteObjects || [],
            marks: st.marks ? Number(st.marks) : undefined,
          })),
        learning_material: (t.learning_material || '')
          .split(',').map((x: string) => x.trim()).filter(Boolean),
        description: t.description,
      })),
  }), [formData, isSuperUser, hasClassListPermission, selectedOrganisation, loggedInOrgId]);

  // Debounced version used only for the preview panel
  // const previewJson = useMemo(() => {
  //   try {
  //     return JSON.stringify(formatData(debouncedFormData), null, 2);
  //   } catch {
  //     return '{}';
  //   }
  // }, [debouncedFormData]);

  const previewJson = useMemo(() => {
    try {
      const full = formatData(debouncedFormData);
      if (!isSuperUser) {
        const { instruction_set, test_instruction_set, question_generator_instruction,
          verification_instruction, step_by_step_solution_instruction,
          step_by_step_reasoning_instruction, sample_example_instruction,
          diagram_instruction, video_instruction, ...rest } = full;
        return JSON.stringify(rest, null, 2);
      }
      return JSON.stringify(full, null, 2);
    } catch {
      return '{}';
    }
  }, [debouncedFormData, formatData, isSuperUser]);

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const data = formatData();
    if (isSuperUser && !selectedOrganisation)
      return showSnackBar('Please select an organisation', 'error');
    if (!isSuperUser && hasClassListPermission && !loggedInOrgId)
      return showSnackBar('Your account has no organisation linked. Please contact admin.', 'error');
    if (!data.subject || !data.class || !data.board)
      return showSnackBar('Fill Subject / Class / Board', 'error');
    if (data.topics.length === 0)
      return showSnackBar('Add at least one topic', 'error');

    setLoading(true);
    try {
      if (isEditMode && editId) {
        await aiActions.updateKnowledge(editId, data);
        showSnackBar('Updated Successfully!', 'success');
      } else {
        await aiActions.createKnowledge(data);
        showSnackBar('Created Successfully!', 'success');
        setFormData(initialFormData);
      }
      setTimeout(() => {
        navigate('/r/student/knowledge-map', {
          state: { selectedSubject: formData.subject, selectedClass: formData.class },
        });
      }, 1500);
    } catch (err) {
      console.error(err);
      showSnackBar('Error saving', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Topic collapse toggle ───────────────────────────────────────────────────
  const toggleTopic = useCallback((idx: number) =>
    setCollapsedTopics((p) => ({ ...p, [idx]: !p[idx] })), []);

  // ─── Recursive Prerequisite Renderer ────────────────────────────────────────
  const renderPrereq = (arr: any[], tIndex: number, stIndex: number, isRoot = false): JSX.Element => (
    <>
      {arr.map((p, idx) => (
        <Box key={p.id} sx={{ pl: 1.5, borderLeft: '2px dashed #e0e0e0', mb: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
            <TextField
              size="small"
              value={p.name}
              onChange={(e) => updatePrereqName(p, e.target.value)}
              placeholder="Prerequisite Name"
              sx={{ ...fieldSx, width: 200 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => addPrereqChild(tIndex, stIndex, p)}
              sx={{ fontSize: '0.68rem', py: 0.3, px: 0.8, minWidth: 'unset', textTransform: 'none' }}
            >
              + Child
            </Button>
            <IconButton size="small" onClick={() => removePrereqObj(arr, idx)}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          {p.children?.length > 0 && renderPrereq(p.children, tIndex, stIndex, false)}
        </Box>
      ))}
      {isRoot && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => addRootPrereq(tIndex, stIndex)}
          sx={{ fontSize: '0.7rem', mt: 0.5, textTransform: 'none' }}
        >
          + Prerequisite
        </Button>
      )}
    </>
  );

  // ─── Section title ───────────────────────────────────────────────────────────
  const sectionTitle = (text: string) => (
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#546e7a', mb: 1 }}>
      {text}
    </Typography>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto', bgcolor: '#f0f2f5', minHeight: '100vh' }}>

      {/* ── TOP HEADER BAR ───────────────────────────────────────────────────── */}
      <Paper elevation={2} sx={{ px: 2.5, py: 1.2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: '#546e7a' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#1565c0' }}>
            {isEditMode ? 'Edit Subject Knowledge Map' : 'Create Subject Knowledge Map'}
          </Typography>
          {isEditMode && (
            <Chip label="Edit Mode" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 18 }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={() => { setFormData(initialFormData); setCollapsedTopics({}); }} sx={{ fontSize: '0.72rem', py: 0.5, textTransform: 'none' }}>
            Reset
          </Button>
          <Button size="small" variant="outlined" onClick={() => navigate(-1)} sx={{ fontSize: '0.72rem', py: 0.5, textTransform: 'none', color: '#546e7a', borderColor: '#90a4ae' }}>
            Cancel
          </Button>
          <LoadingButton
            buttonName={isEditMode ? 'Update' : 'Create'}
            onClickButton={handleSubmit}
            isLoading={loading}
            variant="contained"
            size="small"
            startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.72rem', py: 0.5, textTransform: 'none' }}
          />
        </Box>
      </Paper>

      {/* ── JSON IMPORT ──────────────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ px: 2.5, py: 1.5, mb: 2, borderRadius: 2 }}>
        {sectionTitle('Paste JSON to auto-fill form')}
        <TextField
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder="Paste JSON here..."
          sx={{ ...fieldSx, mb: 1 }}
        />
        <Button size="small" variant="contained" onClick={handleJsonImport} sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
          Import JSON
        </Button>
        <Typography sx={{ fontSize: '0.66rem', color: '#90a4ae', mt: 0.5 }}>
          ℹ️ Instruction set &amp; campaign fields will not be overwritten on import.
        </Typography>
      </Paper>

      {/* ── ORGANISATION + CAMPAIGN + LEAD FORM ──────────────────────────────── */}
      {isSuperUser && (
        <Paper elevation={1} sx={{ px: 2.5, py: 1.5, mb: 2, borderRadius: 2 }}>
          {sectionTitle('Organisation & Campaign')}
          <Grid container spacing={1.5} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontSize: '0.72rem', mb: 0.3, color: '#546e7a', fontWeight: 600 }}>Organisation</Typography>
              <OrganisationDropdown value={selectedOrganisation} onChange={(val) => setSelectedOrganisation(val)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Campaign ID" fullWidth size="small" value={formData.campaign_id} onChange={(e) => updateForm('campaign_id', e.target.value)} placeholder="Enter campaign ID" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Lead Form ID" fullWidth size="small" value={formData.lead_form_id} onChange={(e) => updateForm('lead_form_id', e.target.value)} placeholder="Enter lead form ID" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Test Campaign ID" fullWidth size="small" value={formData.test_campaign_id} onChange={(e) => updateForm('test_campaign_id', e.target.value)} placeholder="Enter test campaign ID" sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Test Lead Form ID" fullWidth size="small" value={formData.test_lead_form_id} onChange={(e) => updateForm('test_lead_form_id', e.target.value)} placeholder="Enter test lead form ID" sx={fieldSx} />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── BASIC INFO ───────────────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ px: 2.5, py: 1.5, mb: 2, borderRadius: 2 }}>
        {sectionTitle('Basic Information')}
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>
            <TextField label="Subject *" fullWidth size="small" value={formData.subject} onChange={(e) => updateForm('subject', e.target.value)} placeholder="Math" sx={fieldSx} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField label="Class *" fullWidth size="small" value={formData.class} onChange={(e) => updateForm('class', e.target.value)} placeholder="11" sx={fieldSx} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField label="Board *" fullWidth size="small" value={formData.board} onChange={(e) => updateForm('board', e.target.value)} placeholder="CBSE" sx={fieldSx} />
          </Grid>
          <Grid item xs={6} md={5}>
            <TextField label="Books (comma separated)" fullWidth size="small" value={formData.books} onChange={(e) => updateForm('books', e.target.value)} placeholder="NCERT, RD Sharma" sx={fieldSx} />
          </Grid>

          {isSuperUser && (
            <>
              <Grid item xs={12} md={6}>
                <TextField label="Instruction Set" fullWidth size="small" multiline rows={2} value={formData.instruction_set} onChange={(e) => updateForm('instruction_set', e.target.value)} placeholder="Enter general instructions for this subject" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Test Instruction Set" fullWidth size="small" multiline rows={2} value={formData.test_instruction_set} onChange={(e) => updateForm('test_instruction_set', e.target.value)} placeholder="Enter test instructions for this subject" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Question Generator Instruction" fullWidth size="small" multiline rows={2} value={formData.question_generator_instruction} onChange={(e) => updateForm('question_generator_instruction', e.target.value)} placeholder="Enter question generator instructions" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Verification Instruction" fullWidth size="small" multiline rows={2} value={formData.verification_instruction} onChange={(e) => updateForm('verification_instruction', e.target.value)} placeholder="Enter verification instructions" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Step by Step Solution Instruction" fullWidth size="small" multiline rows={2} value={formData.step_by_step_solution_instruction} onChange={(e) => updateForm('step_by_step_solution_instruction', e.target.value)} placeholder="Enter step by step solution instructions" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Step by Step Reasoning Instruction" fullWidth size="small" multiline rows={2} value={formData.step_by_step_reasoning_instruction} onChange={(e) => updateForm('step_by_step_reasoning_instruction', e.target.value)} placeholder="Enter step by step reasoning instructions" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Diagram Instruction" fullWidth size="small" multiline rows={2} value={formData.diagram_instruction} onChange={(e) => updateForm('diagram_instruction', e.target.value)} placeholder="Enter diagram instructions for this subject" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Video Instruction" fullWidth size="small" multiline rows={2} value={formData.video_instruction} onChange={(e) => updateForm('video_instruction', e.target.value)} placeholder="Enter video instructions for this subject" sx={fieldSx} />
              </Grid>
              {/* sample_example_instruction kept commented as in original */}
              {/* <Grid item xs={12} md={6}>
                <TextField label="Sample Example Instruction" fullWidth size="small" multiline rows={2}
                  value={formData.sample_example_instruction}
                  onChange={(e) => updateForm('sample_example_instruction', e.target.value)}
                  placeholder="Enter sample example instructions" sx={fieldSx} />
              </Grid> */}
            </>
          )}
        </Grid>
      </Paper>

      {/* ── TOPICS ───────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        {sectionTitle(`Topics (${formData.topics.length})`)}
        {formData.topics.length > 3 && (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
            <Button size="small" sx={{ fontSize: '0.68rem', textTransform: 'none', py: 0.2 }}
              onClick={() => {
                const all: Record<number, boolean> = {};
                formData.topics.forEach((_: any, i: number) => { all[i] = true; });
                setCollapsedTopics(all);
              }}>
              Collapse All
            </Button>
            <Button size="small" sx={{ fontSize: '0.68rem', textTransform: 'none', py: 0.2 }}
              onClick={() => setCollapsedTopics({})}>
              Expand All
            </Button>
          </Box>
        )}
      </Box>

      {formData.topics.map((topic: any, tIndex: number) => {
        const isCollapsed = !!collapsedTopics[tIndex];
        const subtopicCount = topic.subtopics?.length || 0;
        return (
          <Paper key={tIndex} elevation={1} sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', border: '1px solid #e3e8ef' }}>
            {/* Collapsible topic header */}
            <Box
              onClick={() => toggleTopic(tIndex)}
              sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#e8f0fe', cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#d6e4fd' } }}
            >
              <IconButton size="small" sx={{ p: 0.2 }} onClick={(e) => { e.stopPropagation(); toggleTopic(tIndex); }}>
                {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1a3a6e', flexGrow: 1 }}>
                Topic {tIndex + 1}{topic.topic ? `: ${topic.topic}` : ''}
              </Typography>
              <Chip label={`${subtopicCount} subtopic${subtopicCount !== 1 ? 's' : ''}`} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#c5cae9' }} />
              {topic.marks && (
                <Chip label={`${topic.marks} marks`} size="small" color="primary" sx={{ fontSize: '0.65rem', height: 18 }} />
              )}
              {formData.topics.length > 1 && (
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); removeTopic(tIndex); }} sx={{ ml: 0.5 }}>
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
              )}
            </Box>

            {/* Topic body */}
            <Collapse in={!isCollapsed} unmountOnExit>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={5}>
                    <TextField fullWidth size="small" label="Topic Name" value={topic.topic} onChange={(e) => updateForm(`topics.${tIndex}.topic`, e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField fullWidth size="small" label="Topic Prerequisites" value={topic.topic_prerequisites} onChange={(e) => updateForm(`topics.${tIndex}.topic_prerequisites`, e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField fullWidth size="small" type="number" label="Topic Marks" value={topic.marks || ''} onChange={(e) => updateForm(`topics.${tIndex}.marks`, e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Learning Material (comma separated)" value={topic.learning_material} onChange={(e) => updateForm(`topics.${tIndex}.learning_material`, e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Description" value={topic.description} onChange={(e) => updateForm(`topics.${tIndex}.description`, e.target.value)} sx={fieldSx} />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#546e7a', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Subtopics
                </Typography>

                {topic.subtopics.map((subtopic: any, stIndex: number) => (
                  <Box key={stIndex} sx={{ bgcolor: '#fafbff', border: '1px solid #e8ecf4', borderRadius: 1.5, p: 1.5, mb: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth size="small" label="Subtopic Name" value={subtopic.subtopic}
                          onChange={(e) => updateForm(`topics.${tIndex}.subtopics.${stIndex}.subtopic`, e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField
                          fullWidth size="small" type="number" label="Marks" value={subtopic.marks || ''}
                          onChange={(e) => updateForm(`topics.${tIndex}.subtopics.${stIndex}.marks`, e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={6} md={5} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: { sm: 'unset', xs: 'space-between' } }}>
                        <Tooltip title="Add prerequisites">
                          <Button size="small" variant="outlined" startIcon={<AutorenewIcon sx={{ fontSize: 12 }} />} onClick={() => handleAutoGenerate(tIndex, stIndex)} sx={{ fontSize: '0.68rem', py: 0.3, textTransform: 'none', px: 0.8, width: { sm: 'auto', xs: '157px' } }}>
                            Add prerequisites
                          </Button>
                        </Tooltip>
                        <Tooltip title="Clear prerequisites">
                          <IconButton size="small" onClick={() => handleClearAuto(tIndex, stIndex)}>
                            <ClearIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Button size="small" variant="outlined" onClick={() => addSubtopic(tIndex)} sx={{ fontSize: '0.68rem', py: 0.3, textTransform: 'none', px: 0.8, width: { sm: 'auto', xs: '157px' } }}>
                          Add Subtopic
                        </Button>
                        <IconButton size="small" color="error" onClick={() => removeSubtopic(tIndex, stIndex)}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Grid>
                    </Grid>

                    {subtopic.prerequisiteObjects?.length > 0 && (
                      <Box sx={{ mt: 1, pl: 0.5 }}>
                        <Typography sx={{ fontSize: '0.66rem', color: '#78909c', mb: 0.5, fontWeight: 600 }}>
                          Prerequisite Tree
                        </Typography>
                        {renderPrereq(subtopic.prerequisiteObjects, tIndex, stIndex, true)}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        );
      })}

      {/* Add Topic button */}
      <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={addTopic}
        sx={{ py: 1, mb: 2, fontSize: '0.78rem', border: '1.5px dashed #90a4ae', color: '#546e7a', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#e8f0fe', borderColor: '#1976d2', color: '#1976d2' } }}>
        Add Topic
      </Button>

      {/* ── PREVIEW JSON (debounced — won't lag on every keystroke) ─────────── */}
      <Paper elevation={1} sx={{ px: 2.5, py: 1.5, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          {sectionTitle('Preview JSON')}
          <Tooltip title={copiedJson ? 'Copied!' : 'Copy JSON'}>
            <IconButton
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(previewJson);
                setCopiedJson(true);
                setTimeout(() => setCopiedJson(false), 2000);
              }}
              sx={{ color: copiedJson ? '#43a047' : '#546e7a' }}
            >
              {copiedJson
                ? <CheckIcon sx={{ fontSize: 16 }} />
                : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        </Box>
        <Box component="pre" sx={{ overflow: 'auto', maxHeight: 400, bgcolor: '#f5f5f5', p: 2, borderRadius: 1, fontSize: '0.68rem', lineHeight: 1.6 }}>
          {previewJson}
        </Box>
      </Paper>
    </Box>
  );
}